import FileManager from "../utils/file-manager";
import React, { useState, useEffect, useCallback, useRef } from "react";
import Editor from "@monaco-editor/react";
import { ReactComponent as ExpandIcon } from '../../assets/logos/expand.svg';
import { ReactComponent as CollapseIcon } from '../../assets/logos/collapse.svg';
import { ReactComponent as BulbIcon } from '../../assets/logos/bulb.svg';
import { ReactComponent as OptionIcon } from '../../assets/logos/options.svg';
import { ReactComponent as ComplexityIcon } from '../../assets/logos/complexity.svg';
import {
    Button,
    Space,
    message,
    Spin,
    Modal,
    Tooltip,
    Badge,
    Skeleton,
    Tabs,
    notification,
    Dropdown
} from "antd";

import {
    FullscreenOutlined,
    FullscreenExitOutlined,
    LoadingOutlined,
    UploadOutlined,
    SaveOutlined,
    DownOutlined,
    UsbOutlined,
    PlayCircleOutlined,
} from "@ant-design/icons";
import ArduinoSimulator from "./arduino-simulator/ArduinoSimulator";
import { useParams } from "react-router-dom";
import {
    saveFileInIde,
    getFileContent,
    getIdeProjectDetails,
    updateIdeProject,
} from "../../services/ide-apis";
import { compileArduinoSketch, compileSketchForBoard, uploadHexToBoard, uploadBinToEspBoard, selectSerialPort, SUPPORTED_BOARDS, getCodeSuggestions } from "../../services/arduino-service";
import {
    trackEvent,
    getCssClassNameForAutoSize,
    isMobile,
} from "../utils/util-functions";
import { global_var } from "../../services/cs-lab-config";
import IdeHeader from "./ide-header";
import { buildFileContentMap, isProjectEmptyFromMap, hasRealCode } from "./ide-empty-check";
import CreateIDEProjectModal from "./create-ide-project-modal";
import InlineAiPrompt from "./inlineAiPrompt/inline-ai-prompt";
import './python-ide.css';
import AiChatBot from "./aiChatBot/ai-chat-bot";
import ArduinoLibrariesLoader from "../common/arduino-libraries-loader";
import SerialMonitor from "./serial-monitor";


const ArduinoIDE = () => {
    document.title = "CS Cloud Lab | Arduino IDE";

    let { ideProjectId } = useParams();

    let hideHeader = new URLSearchParams(window.location.search).get(
        "hideHeader"
    );

    let isSaveDisabledInIDE = new URLSearchParams(window.location.search).get(
        "isSaveDisabledInIDE"
    );

    let isEditAndSaveAllowedInIDE = new URLSearchParams(window.location.search).get(
        "isEditAndSaveAllowedInIDE"
    );

    const [selectedTheme, setSelectedTheme] = useState("light");
    const [ideProjectDetails, setIdeProjectDetails] = useState(undefined);
    const updateProjectDetails = useCallback((updatedProject) => {
        setIdeProjectDetails({ ...updatedProject });
    }, []);

    const [selectedFile, setSelectedFile] = useState(undefined);
    const [inputCode, setInputCode] = useState("");

    const [readOnlyMode, setReadOnlyMode] = useState(false);

    const [outputWindowExpanded, setOutputWindowExpanded] = useState(false);
    const [fileManagerExpanded, setFileManagerExpanded] = useState(true);

    const [newProjectModal, setNewProjectModal] = useState(false);
    const [mobileFileManagerModal, setMobileFileManagerModal] = useState(false);

    const [fileLoadingInProgress, setFileLoadingInProgress] = useState(false);
    const [fileSavingInProgress, setFileSavingInProgress] = useState(false);
    const [uploadInProgress, setUploadInProgress] = useState(false);       // compile phase
    const [boardUploadInProgress, setBoardUploadInProgress] = useState(false); // serial flash phase

    const [unsavedChangePresent, setUnsavedChangePresent] = useState(false);

    const [fileMetaList, setFileMetaList] = useState([]);
    const [fileHasContentMap, setFileHasContentMap] = useState({});

    const [selectedLibrariesList, setSelectedLibrariesList] = useState([]);
    const [selectedBoardId, setSelectedBoardId] = useState('uno');

    const [cursorPosition, setCursorPosition] = useState(null);
    const [editorInstance, setEditorInstance] = useState(null);
    const monacoRef = useRef(null);
    const [showInlineAiPrompt, setShowInlineAiPrompt] = useState(false);
    const [tabActiveKey, setTabActiveKey] = useState('Console');
    const [enabledAIfeatures, setEnabledAIfeatures] = useState([]);

    const [uploadOutput, setUploadOutput] = useState("");
    const [showOutputWindow, setShowOutputWindow] = useState(false);
    const [simulatorHex, setSimulatorHex] = useState(null);
    const [simulateInProgress, setSimulateInProgress] = useState(false);
    const [boardConnected, setBoardConnected] = useState(false);
    const [isSaveDisable, setIsSaveDisable] = useState(false);

    const autoSaveTimerRef = useRef(null);
    const outputRef = useRef(null);

    const tabs = ['Console', 'Serial Monitor'];
    if (global_var.role_id !== global_var.STUDENT_ROLE_ID || enabledAIfeatures.length) {
        tabs.push('Ai Chatbot');
    }

    useEffect(() => {
        if (isSaveDisabledInIDE === "true") {
            setIsSaveDisable(true);
        } else {
            setIsSaveDisable(false);
        }
    }, [isSaveDisabledInIDE]);

    useEffect(() => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [uploadOutput]);

    const handleSelectedTheme = () => {
        const themeName = selectedTheme === 'light' ? 'vs-dark' : 'light';

        trackEvent("ide_dark_mode_toggle_clicked", {
            ide_project_stack_name: "arduino",
            ide_theme_value: themeName,
        });

        setSelectedTheme(themeName);
    };

    const handleCodeChange = (value) => {
        setInputCode(value);
        setUnsavedChangePresent(true);
        setFileHasContentMap((prev) => ({
            ...prev,
            [selectedFile.id]: hasRealCode(value, selectedFile.fileName),
        }));
        if (editorInstance) {
            var position = editorInstance.getPosition();
            setCursorPosition(position);
        }

        if (isSaveDisable) {
            return
        };

        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(() => {
            autoSaveFile(value);
        }, 2000);
    };

    function toggleOutputWindow() {
        setOutputWindowExpanded(!outputWindowExpanded);
    }

    const navigateToFile = useCallback(
        (event) => {
            if (!ideProjectId) {
                return;
            }

            if (event.node.fileMimeType === "image") {
                setOutputWindowExpanded(false);
                setSelectedFile(event.node);
                setUnsavedChangePresent(false);
            } else {
                setSelectedFile(undefined);
                setFileLoadingInProgress(true);

                getFileContent(ideProjectId, event.node.id).then((response) => {
                    setFileLoadingInProgress(false);

                    response = response.data;

                    if (response.statusCode === 200) {
                        event.node.fileContent = response.fileContent;
                        setInputCode(event.node.fileContent);
                        setSelectedFile(event.node);
                        setUnsavedChangePresent(false);
                    } else {
                        message.error(response.message);
                    }
                });
            }
        },
        [ideProjectId]
    );

    const nodeSelected = useCallback(
        (nodeIds, event) => {
            if (!event.node.isFile) {
                return;
            }

            if (unsavedChangePresent) {
                Modal.confirm({
                    title: "There are unsaved changes in your editor",
                    cancelText: "Leave editor",
                    okText: "Back to editor",
                    onCancel() {
                        navigateToFile(event);
                    },
                });
                return;
            }

            navigateToFile(event);
        },
        [unsavedChangePresent, navigateToFile]
    );

    const handleNewFile = useCallback(
        (fileMetaListObj, updatedTreeData) => {
            if (!fileMetaListObj.isFile) {
                return;
            }

            setFileMetaList(updatedTreeData);
            setFileHasContentMap((prev) => ({ ...prev, [fileMetaListObj.id]: false }));

            navigateToFile({ node: fileMetaListObj });
        },
        [navigateToFile]
    );

    const handleFileDeleted = useCallback(
        (deletedNode, updatedTreeData) => {
            setFileMetaList(updatedTreeData);
            setFileHasContentMap((prev) => {
                const newMap = { ...prev };
                delete newMap[deletedNode.id];
                return newMap;
            });

            if (deletedNode.id === (selectedFile ? selectedFile.id : false)) {
                setSelectedFile(undefined);
            }
        },
        [selectedFile, setSelectedFile]
    );

    const handleFileRenamed = useCallback(
        (newTreeData, newFileMetadata) => {
            if (newFileMetadata.id === (selectedFile ? selectedFile.id : false)) {
                setSelectedFile((oldFileObj) => {
                    const newObj = {
                        ...oldFileObj,
                        fileName: newFileMetadata.fileMetaListObj.fileName,
                        fileMimeType: newFileMetadata.fileMetaListObj.fileMimeType,
                    };
                    if (newFileMetadata.fileMetaListObj.fileS3reference) {
                        newObj.fileS3reference =
                            newFileMetadata.fileMetaListObj.fileS3reference;
                    }
                    return newObj;
                });
                setFileHasContentMap((prev) => ({
                    ...prev,
                    [newFileMetadata.id]: hasRealCode(inputCode, newFileMetadata.fileMetaListObj.fileName),
                }));
            }
        },
        [selectedFile, setSelectedFile, inputCode]
    );

    async function autoSaveFile(value) {
        saveFileInIde(ideProjectId, selectedFile.id, {
            fileContent: value,
            updateS3Content: true,
            fileOrFolderObject: {},
        }).then((response) => {
            response = response.data;

            if (response.statusCode === 200) {
                setUnsavedChangePresent(false);
                const newTree = updateFileContentInTree(fileMetaList, selectedFile.id, value);
                setFileMetaList(newTree);
                setFileHasContentMap((prev) => ({
                    ...prev,
                    [selectedFile.id]: hasRealCode(value, selectedFile.fileName),
                }));
            }
        }).catch(() => { });
    }

    const updateFileContentInTree = (fileTree, fileId, newContent) => {
        return fileTree.map(node => {
            if (node.id === fileId) {
                return { ...node, fileContent: newContent };
            }
            if (node.children) {
                return { ...node, children: updateFileContentInTree(node.children, fileId, newContent) };
            }
            return node;
        });
    };

    async function saveFileInIdeHandler(chatbotInput) {

        if (isSaveDisable) {
            return
        };
        if (chatbotInput) {
            trackEvent("chatbot_insert_button_clicked", {
                ide_project_stack_name: "arduino",
            });
        }
        setFileSavingInProgress(true);

        return saveFileInIde(ideProjectId, selectedFile.id, {
            fileContent: chatbotInput ? chatbotInput : inputCode,
            updateS3Content: true,
            fileOrFolderObject: {},
        }).then((response) => {
            setFileSavingInProgress(false);
            response = response.data;

            if (response.statusCode === 200) {
                const newTree = updateFileContentInTree(fileMetaList, selectedFile.id, chatbotInput ? chatbotInput : inputCode);
                setFileMetaList(newTree);
                setFileHasContentMap((prev) => ({
                    ...prev,
                    [selectedFile.id]: hasRealCode(chatbotInput ? chatbotInput : inputCode, selectedFile.fileName),
                }));

                message.success(response.message);
                setUnsavedChangePresent(false);
            } else {
                message.error(response.message);
            }
            return response;
        });
    }

    function handleSaveFile() {
        if (!unsavedChangePresent) {
            message.info("There are no changes to save");
            return;
        }

        trackEvent("ide_save_button_clicked", {
            ide_project_stack_name: "arduino",
        });

        saveFileInIdeHandler();
    }

    async function handleUploadToBoard() {

        if (!('serial' in navigator)) {
            return message.error('Web Serial API is not supported in this browser. Please use Chrome or Edge.');
        }

        if (!selectedFile) {
            message.error("Please select a file to upload.");
            return;
        }

        // Pick the port first: the browser only shows the port picker while handling the click,
        // and that window has passed once saving and compiling finish.
        let selectedPort;
        try {
            selectedPort = await selectSerialPort();
        } catch (_) {
            message.error("No board selected. Please choose your board's port to upload.");
            return;
        }

        if (unsavedChangePresent) {
            trackEvent("ide_upload_clicked_without_save", {
                ide_project_stack_name: "arduino",
            });
            await saveFileInIdeHandler();
        }

        trackEvent("ide_upload_to_board_clicked", {
            ide_project_stack_name: "arduino",
        });

        setUploadInProgress(true);
        setShowOutputWindow(true);
        setOutputWindowExpanded(true);
        setTabActiveKey('Console');
        setUploadOutput("Compiling sketch...\n");

        const sketchFiles = [{ name: selectedFile.fileName, content: inputCode }];
        const board = SUPPORTED_BOARDS[selectedBoardId];
        const isEspBoard = board.platform === "esp8266";

        compileSketchForBoard(sketchFiles, selectedBoardId, selectedLibrariesList)
            .then(async (result) => {
                const body = result.body || result;

                // Collect any compiler stdout / stderr
                const compilerOutput = [
                    body.stdout || "",
                    body.stderr || "",
                ]
                    .filter(Boolean)
                    .join("\n");

                const success = body.success === true || result.statusCode === 200;

                const firmware = isEspBoard ? body.bin : body.hex;

                if (success && firmware) {
                    let flashBytes = 0;
                    let maxFlashBytes = 32256;
                    try {
                        if (isEspBoard) {
                            // The .bin is the raw flash image, so its size is the sketch size
                            flashBytes = atob(body.bin).length;
                            maxFlashBytes = board.maxSketchBytes;
                        } else {
                            // Decode base64 → raw Intel HEX text to estimate flash usage
                            const hexText = atob(body.hex);
                            const dataRecords = hexText
                                .split(/\r?\n/)
                                .filter(line => line.startsWith(':'));
                            dataRecords.forEach(line => {
                                const recType = parseInt(line.substring(7, 9), 16);
                                if (recType === 0) {
                                    flashBytes += parseInt(line.substring(1, 3), 16);
                                }
                            });
                        }
                    } catch (_) { /* ignore decode errors */ }

                    const flashKb = (flashBytes / 1024).toFixed(2);
                    const pct = ((flashBytes / maxFlashBytes) * 100).toFixed(1);

                    setUploadInProgress(false);

                    const compileLog =
                        `Compiling sketch...\n` +
                        (compilerOutput ? compilerOutput + "\n" : "") +
                        `\nSketch uses ${flashBytes} bytes (${pct}%) of program storage space. Maximum is ${maxFlashBytes} bytes (${flashKb} KB).\n` +
                        `\n\u2713 Compilation successful!\n` +
                        `\n──────────────────────────────────\n` +
                        `Uploading to board...\n`;

                    setUploadOutput(compileLog);

                    // ── Serial flash phase ────────────────────────────────
                    setBoardUploadInProgress(true);
                    try {
                        const onStatusUpdate = (statusMsg) => {
                            setUploadOutput(prev => prev + statusMsg);
                        };
                        if (isEspBoard) {
                            await uploadBinToEspBoard(body.bin, board.espConfig, onStatusUpdate, selectedPort);
                        } else {
                            await uploadHexToBoard(body.hex, board.stkConfig, onStatusUpdate, selectedPort);
                        }
                        setBoardConnected(true);
                    } catch (uploadErr) {
                        const detail = uploadErr?.message || "Unknown upload error.";
                        setUploadOutput(prev =>
                            prev + `\n\u2717 Upload failed: ${detail}\n`
                        );
                    } finally {
                        setBoardUploadInProgress(false);
                    }

                } else if (success) {
                    setUploadOutput(
                        `Compiling sketch...\n${compilerOutput}\n\n\u2713 Compilation successful!`
                    );
                } else {
                    const errMsg = body.error || body.message || "Compilation failed.";
                    setUploadOutput(
                        `Compiling sketch...\n${compilerOutput}\n\n\u2717 ${errMsg}`
                    );
                }
            })
            .catch((err) => {
                const errDetail =
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unknown error occurred.";
                setUploadOutput(
                    `Compiling sketch...\n\n\u2717 Compiler error:\n${errDetail}`
                );
            })
            .finally(() => {
                setUploadInProgress(false);
            });
    }

    async function handleSimulate() {
        if (!selectedFile) {
            message.error("Please select a file to simulate.");
            return;
        }

        if (unsavedChangePresent) {
            await saveFileInIdeHandler();
        }

        trackEvent("ide_simulate_clicked", {
            ide_project_stack_name: "arduino",
        });

        setSimulateInProgress(true);
        setShowOutputWindow(true);
        setOutputWindowExpanded(true);
        setTabActiveKey('Console');
        setUploadOutput("Compiling sketch for simulation...\n");

        const sketchFiles = [{ name: selectedFile.fileName, content: inputCode }];

        compileArduinoSketch(sketchFiles, SUPPORTED_BOARDS[selectedBoardId].fqbn, selectedLibrariesList)
            .then((result) => {
                const body = result.body || result;
                const compilerOutput = [
                    body.stdout || "",
                    body.stderr || "",
                ].filter(Boolean).join("\n");

                const success = body.success === true || result.statusCode === 200;

                if (success && body.hex) {
                    setUploadOutput(
                        `Compiling sketch for simulation...\n` +
                        (compilerOutput ? compilerOutput + "\n" : "") +
                        `\n\u2713 Compilation successful! Launching simulator...\n`
                    );
                    setSimulatorHex(body.hex);
                } else {
                    const errMsg = body.error || body.message || "Compilation failed.";
                    setUploadOutput(
                        `Compiling sketch for simulation...\n${compilerOutput}\n\n\u2717 ${errMsg}`
                    );
                }
            })
            .catch((err) => {
                const errDetail =
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unknown error occurred.";
                setUploadOutput(
                    `Compiling sketch for simulation...\n\n\u2717 Compiler error:\n${errDetail}`
                );
            })
            .finally(() => {
                setSimulateInProgress(false);
            });
    }

    function getCssClassName(component) {
        return getCssClassNameForAutoSize(
            component,
            fileManagerExpanded,
            outputWindowExpanded
        );
    }

    useEffect(() => {
        if (!ideProjectDetails) {
            return;
        }

        const findNode = (tData, fileId, dataObj) => {
            for (let i = 0; i < tData.length; i++) {
                const node = tData[i];
                if (node.id === fileId) {
                    dataObj["node"] = node;
                    return true;
                }
                if (node.children) {
                    if (findNode(node.children, fileId, dataObj)) {
                        return true;
                    }
                }
            }
            return false;
        };

        if (ideProjectDetails.lastAccessedFileId) {
            const dataObj = {};

            findNode(
                ideProjectDetails.fileMetaList,
                ideProjectDetails.lastAccessedFileId,
                dataObj
            );

            if (dataObj.node) {
                navigateToFile({ node: dataObj.node });
            }
        }
    }, [ideProjectDetails, navigateToFile]);

    useEffect(() => {
        if (ideProjectId) {
            setNewProjectModal(false);
            getIdeProjectDetails(ideProjectId).then((response) => {
                response = response.data;

                if (response.statusCode === 200) {
                    
                    let isOwner = response.projectDetails.fkUserLoginId === global_var.user_login_id
                    let isProject = response?.projectDetails?.fkIdeProjectTypeName === "Project" || isEditAndSaveAllowedInIDE === "true";
                    let isNotStudent = global_var.role_id !== global_var.STUDENT_ROLE_ID;
                    let conditionObj = {
                        isOwner,
                        isProject,
                        isNotStudent
                    }

                    let checkObj;

                    if (isProject) {
                        checkObj = ['isProject'];
                    } else {
                        checkObj = ['isOwner', 'isNotStudent'];
                    }
                    if (
                        checkObj.some(key => conditionObj[key])
                    ) {

                        const shouldSetReadOnly =
                            response.projectDetails.evaluationStatus === "Evaluated" ||
                            response.projectDetails.fkUserLoginId !== global_var.user_login_id ||
                            response.projectDetails.editDisabled;

                        if (shouldSetReadOnly) {
                            setReadOnlyMode(true);
                        }

                        if (isEditAndSaveAllowedInIDE === 'true') {
                            setReadOnlyMode(false);
                        }
                        setIdeProjectDetails(response.projectDetails);

                        if (response.projectDetails.packageList) {
                            setSelectedLibrariesList(response.projectDetails.packageList);
                        }

                        if (response.projectDetails.fileMetaList) {
                            setFileMetaList(response.projectDetails.fileMetaList);
                            setFileHasContentMap(buildFileContentMap(response.projectDetails.fileMetaList));
                        }
                    } else {
                        message.error("you are not authorised");
                    }
                } else {
                    message.error(response.message);
                }
            });
        } else {
            setIdeProjectDetails(undefined);
            setSelectedFile(undefined);
            setNewProjectModal(true);
            setShowOutputWindow(false);
            setOutputWindowExpanded(false);
        }
    }, [ideProjectId, isEditAndSaveAllowedInIDE]);

    const openMobileFileManagerModal = () => {
        setMobileFileManagerModal(true);
    };

    const closeMobileFileManagerModal = () => {
        setMobileFileManagerModal(false);
    };

    const handleEditorDidMount = (editor, monacoInstance) => {
        setEditorInstance(editor);
        monacoRef.current = monacoInstance;
        const disablePaste = new URLSearchParams(window.location.search).get("disablePaste");

        if (disablePaste && editor) {
            const noop = () => { };

            editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyV, noop);
            editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyC, noop);

            editor.onDidPaste(() => {
                editor.trigger('keyboard', 'undo', null);
            });

            const domNode = editor.getDomNode();
            if (domNode) {
                const prevent = (ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    return false;
                };

                domNode.addEventListener("paste", prevent, true);
                domNode.addEventListener("copy", prevent, true);
                domNode.addEventListener(
                    "beforeinput",
                    (ev) => {
                        if (ev.inputType && ev.inputType.toLowerCase().includes("paste")) {
                            prevent(ev);
                        }
                    },
                    true
                );
                domNode.addEventListener("contextmenu", prevent, true);
            }
        }

        if (global_var.role_id !== global_var.STUDENT_ROLE_ID || enabledAIfeatures.includes('ai code generator')) {
            editor.addAction({
                id: "ai-generate",
                label: "Ai generate",
                precondition: null,
                keybindingContext: null,
                contextMenuGroupId: "navigation",
                contextMenuOrder: 1.5,
                run: function () {
                    setShowInlineAiPrompt(true);
                },
            });
        }

        if (!window.arduinoCompletionRegistered) {
            monacoInstance.languages.registerCompletionItemProvider("cpp", {
                provideCompletionItems: function (model, position) {
                    const word = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.startColumn,
                        endColumn: word.endColumn,
                    };

                    return { suggestions: getCodeSuggestions(monacoInstance, range) };
                },
            });
            window.arduinoCompletionRegistered = true;
        }

        if (editorInstance) {
            editorInstance.focus();
        }
    };

    const submittedLibraries = (libraryList) => {
        updateIdeProject(ideProjectId, {
            packageList: libraryList
        });
        setSelectedLibrariesList(libraryList);

        trackEvent("ide_library_submitted", {
            ide_project_stack_name: "arduino"
        });
    };

    const insertCodeAtLine = (lineNumber, code) => {
        if (editorInstance && cursorPosition && monacoRef.current) {
            const range = new monacoRef.current.Range(lineNumber, 1, lineNumber, 1);
            const model = editorInstance.getModel();
            model.pushStackElement();
            const edit = {
                range: range,
                text: code,
                forceMoveMarkers: true,
            };

            editorInstance.executeEdits('insertCode', [edit]);
            model.pushStackElement();
            const codeLines = code.split('\n');
            const nextLine = (lineNumber + codeLines.length) - 1;
            const position = { lineNumber: nextLine, column: 1 };
            editorInstance.setPosition(position);
            setCursorPosition(position);
            setUnsavedChangePresent(true);
            const updatedCode = model.getValue();
            saveFileInIdeHandler(updatedCode);
        }
    };

    const handlePromptInput = (generatedCode) => {
        if (cursorPosition) {
            insertCodeAtLine(cursorPosition.lineNumber, generatedCode);
        }
    };

    const handleAcceptAiCode = () => {
        setShowInlineAiPrompt(false);
    };

    const handleRejectAiCode = () => {
        if (editorInstance) {
            const model = editorInstance.getModel();
            model.undo();
            setShowInlineAiPrompt(false);
        }
    };

    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && showInlineAiPrompt) {
                if (editorInstance) {
                    const model = editorInstance.getModel();
                    model.undo();
                    setShowInlineAiPrompt(false);
                    let newPosition = editorInstance.getPosition();
                    setCursorPosition(newPosition);
                }
            }
        };

        window.addEventListener('keydown', handleEscapeKey);
        return () => {
            window.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showInlineAiPrompt, editorInstance]);

    const handleChatBotInsert = (codeToInsert) => {
        if (editorInstance) {
            var position = editorInstance.getPosition();
            setCursorPosition(position);
        }
        if (cursorPosition ? cursorPosition.lineNumber : false) {
            insertCodeAtLine(cursorPosition.lineNumber, codeToInsert);
        } else {
            notification.error({
                message: 'Error',
                description: 'Please select the line you wanted to insert the code',
            });
        }
    };

    const handleChatbotVisibility = () => {
        setShowOutputWindow(true);
        setOutputWindowExpanded(true);
        setUploadOutput("");
        trackEvent("ide_ai_chatbot_clicked", {
            ide_project_stack_name: "arduino",
        });
        setTabActiveKey('Ai Chatbot');
    };

    const handleTabChange = (key) => {
        setTabActiveKey(key);
    };

    useEffect(() => {
        if (global_var.allowedAiFeatures) {
            setEnabledAIfeatures(global_var.allowedAiFeatures);
        }
    }, []);

    // Detect board connection via Web Serial API
    useEffect(() => {
        if (!('serial' in navigator)) return;

        const checkPorts = async () => {
            try {
                const ports = await navigator.serial.getPorts();
                setBoardConnected(ports.length > 0);
            } catch (_) { }
        };

        checkPorts();

        const onConnect = () => setBoardConnected(true);
        const onDisconnect = () => {
            setBoardConnected(false);
            notification.warning({
                message: 'Board Disconnected',
                description: 'Please reconnect the board'
            });
            // Re-check after a brief delay in case multiple ports exist
            setTimeout(async () => {
                try {
                    const ports = await navigator.serial.getPorts();
                    setBoardConnected(ports.length > 0);
                } catch (_) { }
            }, 300);
        };

        navigator.serial.addEventListener('connect', onConnect);
        navigator.serial.addEventListener('disconnect', onDisconnect);

        return () => {
            navigator.serial.removeEventListener('connect', onConnect);
            navigator.serial.removeEventListener('disconnect', onDisconnect);
        };
    }, []);

    const boardItems = Object.entries(SUPPORTED_BOARDS).map(([boardId, config]) => ({
        key: boardId,
        label: config.name,
        onClick: () => setSelectedBoardId(boardId)
    }));

    return (
        <div className="container-fluid p-0">
            <div className="row mt-1">
                <div className="col">
                    {ideProjectDetails && hideHeader !== "true" && (
                        <IdeHeader
                            headerName="CS Arduino IDE"
                            showInPortFolio={ideProjectDetails.showInPortFolio}
                            submittedForEvaluation={ideProjectDetails.submittedForEvaluation}
                            ideProjectDetails={ideProjectDetails}
                            fkResourceId={ideProjectDetails.fkResourceId}
                            unsavedChangePresent={unsavedChangePresent}
                            readOnlyMode={readOnlyMode}
                            onUpdateProjectDetails={updateProjectDetails}
                            isProjectEmpty={isProjectEmptyFromMap(fileHasContentMap)}
                        />
                    )}

                    <div className="row mt-2 rounded-top-left pt-2 bg-white">
                        {ideProjectDetails ? (
                            <div
                                className={getCssClassName("file-manager")}
                                style={{ height: "85vh" }}
                            >
                                <FileManager
                                    fileStructure={fileMetaList}
                                    defaultSelectedKey={ideProjectDetails.lastAccessedFileId}
                                    ideProjectId={ideProjectId}
                                    ideProjectDetails={ideProjectDetails}
                                    nodeSelected={nodeSelected}
                                    handleNewFile={handleNewFile}
                                    handleFileDeleted={handleFileDeleted}
                                    handleFileRenamed={handleFileRenamed}
                                    readOnlyMode={readOnlyMode}
                                    projectStack="arduino"
                                    uploadAcceptFileTypes=".ino, .cpp, .h, .c, .txt"
                                />
                            </div>
                        ) : (
                            false
                        )}

                        {fileLoadingInProgress && (
                            <div className="col-10 text-center mt-3">
                                <Skeleton active="true" paragraph={{ rows: 8 }} />
                            </div>
                        )}

                        {selectedFile ? (
                            <div className={getCssClassName("code-editor-output-container")}>
                                <div className="row mb-1">
                                    <div className={getCssClassName("code-editor")}>
                                        <div className="row mb-1">
                                            <div className="col">
                                                <Space direction="vertical">
                                                    <Space wrap>
                                                        {isMobile ? (
                                                            <OptionIcon
                                                                onClick={() => openMobileFileManagerModal()}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        ) : fileManagerExpanded ? (
                                                            <ExpandIcon
                                                                onClick={() => setFileManagerExpanded(false)}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        ) : (
                                                            <CollapseIcon
                                                                onClick={() => setFileManagerExpanded(true)}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        )}

                                                        <span className="badge" style={{ backgroundColor: '#404040' }}>
                                                            {selectedFile.fileName}
                                                        </span>

                                                        {unsavedChangePresent && (
                                                            <Tooltip
                                                                placement="topLeft"
                                                                title="There are unsaved changes in your editor"
                                                            >
                                                                <Badge status="error" />
                                                            </Tooltip>
                                                        )}
                                                    </Space>
                                                </Space>

                                                {selectedFile.fileMimeType !== "image" &&
                                                    !fileLoadingInProgress ? (
                                                    <Space direction="vertical" className="float-end">
                                                        <Space wrap>
                                                            {(global_var.role_id !== global_var.STUDENT_ROLE_ID || enabledAIfeatures.includes('Ai chatbot')) &&
                                                                !outputWindowExpanded &&
                                                                <Button
                                                                    style={{
                                                                        backgroundColor: 'white',
                                                                        color: 'black',
                                                                        border: '1px solid #eeeeee',
                                                                        height: '32px',
                                                                        borderRadius: '5px'
                                                                    }}
                                                                    onClick={handleChatbotVisibility}
                                                                >
                                                                    <ComplexityIcon /> Ai chatbot
                                                                </Button>
                                                            }

                                                            {!readOnlyMode && (
                                                                <Dropdown menu={{ items: boardItems }} trigger={['click']}>
                                                                    <Button style={{ color: '#404040', width: '145px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            <UsbOutlined style={{ color: boardConnected ? '#52c41a' : '#aaa', marginRight: '8px' }} />
                                                                            {SUPPORTED_BOARDS[selectedBoardId].name}
                                                                        </div>
                                                                        <DownOutlined />
                                                                    </Button>
                                                                </Dropdown>
                                                            )}

                                                            {!readOnlyMode && (
                                                                <ArduinoLibrariesLoader
                                                                    submittedLibraries={submittedLibraries}
                                                                    selectedLibrariesList={selectedLibrariesList}
                                                                    ideProjectStackName="arduino"
                                                                />
                                                            )}

                                                            {(!readOnlyMode && !isSaveDisable) && (
                                                                outputWindowExpanded ? (
                                                                    <Tooltip title="Save">
                                                                        <Button
                                                                            style={{ backgroundColor: '#4235A1' }}
                                                                            loading={fileSavingInProgress}
                                                                            className="float-end"
                                                                            type="primary"
                                                                            onClick={handleSaveFile}
                                                                            size="small"
                                                                            icon={<SaveOutlined />}
                                                                        />
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Button
                                                                        style={{ backgroundColor: '#4235A1' }}
                                                                        loading={fileSavingInProgress}
                                                                        className="float-end"
                                                                        type="primary"
                                                                        onClick={handleSaveFile}
                                                                        size="small"
                                                                        icon={<SaveOutlined />}
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                )
                                                            )}

                                                            {!readOnlyMode && selectedFile.fileMimeType === "arduino" && (
                                                                outputWindowExpanded ? (
                                                                    <Tooltip title={uploadInProgress ? "Compiling…" : boardUploadInProgress ? "Uploading…" : "Upload to Board"}>
                                                                        <Button
                                                                            style={{ backgroundColor: '#00875A' }}
                                                                            loading={uploadInProgress || boardUploadInProgress}
                                                                            disabled={uploadInProgress || boardUploadInProgress}
                                                                            className="float-end"
                                                                            type="primary"
                                                                            onClick={handleUploadToBoard}
                                                                            size="small"
                                                                            icon={<UploadOutlined />}
                                                                        />
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Button
                                                                        style={{ backgroundColor: '#00875A' }}
                                                                        loading={uploadInProgress || boardUploadInProgress}
                                                                        disabled={uploadInProgress || boardUploadInProgress}
                                                                        className="float-end"
                                                                        type="primary"
                                                                        onClick={handleUploadToBoard}
                                                                        size="small"
                                                                        icon={<UploadOutlined />}
                                                                    >
                                                                        {uploadInProgress
                                                                            ? "Compiling…"
                                                                            : boardUploadInProgress
                                                                                ? "Uploading…"
                                                                                : "Upload to Board"}
                                                                    </Button>
                                                                )
                                                            )}

                                                            {!readOnlyMode && selectedFile.fileMimeType === "arduino" && selectedBoardId === 'uno' && (
                                                                outputWindowExpanded ? (
                                                                    <Tooltip title={simulateInProgress ? "Compiling…" : "Simulate"}>
                                                                        <Button
                                                                            style={{ backgroundColor: '#00ACC1' }}
                                                                            loading={simulateInProgress}
                                                                            disabled={simulateInProgress || uploadInProgress || boardUploadInProgress}
                                                                            className="float-end"
                                                                            type="primary"
                                                                            onClick={handleSimulate}
                                                                            size="small"
                                                                            icon={<PlayCircleOutlined />}
                                                                        />
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Button
                                                                        style={{ backgroundColor: '#00ACC1' }}
                                                                        loading={simulateInProgress}
                                                                        disabled={simulateInProgress || uploadInProgress || boardUploadInProgress}
                                                                        className="float-end"
                                                                        type="primary"
                                                                        onClick={handleSimulate}
                                                                        size="small"
                                                                        icon={<PlayCircleOutlined />}
                                                                    >
                                                                        {simulateInProgress ? "Compiling…" : "Simulate"}
                                                                    </Button>
                                                                )
                                                            )}

                                                            <Button
                                                                className="float-end"
                                                                style={{ backgroundColor: '#FFC200' }}
                                                                type="primary"
                                                                size="small"
                                                                onClick={handleSelectedTheme}
                                                            >
                                                                {selectedTheme === 'light' ? <BulbIcon style={{ fontSize: '16px' }} /> : <BulbIcon />}
                                                            </Button>

                                                            {showOutputWindow &&
                                                                (outputWindowExpanded ? (
                                                                    <FullscreenExitOutlined
                                                                        onClick={toggleOutputWindow}
                                                                        style={{ fontSize: "1.5rem" }}
                                                                    />
                                                                ) : (
                                                                    <FullscreenOutlined
                                                                        onClick={toggleOutputWindow}
                                                                        style={{ fontSize: "1.5rem" }}
                                                                    />
                                                                ))}
                                                        </Space>
                                                    </Space>
                                                ) : (
                                                    false
                                                )}
                                            </div>
                                        </div>

                                        <div className="row mb-1">
                                            {selectedFile.fileMimeType === "image" ? (
                                                <div className="col text-center">
                                                    <img
                                                        alt={selectedFile.fileName}
                                                        className="img-thumbnail img-fluid"
                                                        src={selectedFile.fileS3reference}
                                                    ></img>
                                                </div>
                                            ) : (
                                                <div className="col p-0" style={{ overflowX: "auto", position: "relative" }}>
                                                    <Editor
                                                        height={isMobile ? "45vh" : "80vh"}
                                                        onChange={handleCodeChange}
                                                        defaultLanguage={
                                                            selectedFile.fileMimeType === "arduino"
                                                                ? "cpp"
                                                                : selectedFile.fileMimeType
                                                        }
                                                        theme={selectedTheme}
                                                        defaultValue={
                                                            selectedFile.fileMimeType === "arduino"
                                                                ? "\n\nvoid setup() {\n\n}\nvoid loop() {\n\n}"
                                                                : ""
                                                        }
                                                        value={
                                                            selectedFile.fileContent
                                                                ? selectedFile.fileContent
                                                                : (selectedFile.fileMimeType === "arduino"
                                                                    ? "\n\nvoid setup() {\n\n}\nvoid loop() {\n\n}"
                                                                    : "")
                                                        }
                                                        onMount={handleEditorDidMount}
                                                        options={{ readOnly: readOnlyMode }}
                                                    />

                                                    {showInlineAiPrompt &&
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: `calc(50% - 250px)`,
                                                                left: `calc(50% - 300px)`,
                                                                zIndex: 1000,
                                                            }}
                                                        >
                                                            <InlineAiPrompt
                                                                ProjectLanguage="Arduino (C++)"
                                                                cursorPosition={cursorPosition}
                                                                handlePrompt={handlePromptInput}
                                                                handleCodeAcceptHandler={handleAcceptAiCode}
                                                                handleRejectHandler={handleRejectAiCode}
                                                                selectedTheme={selectedTheme}
                                                            />
                                                        </div>
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={getCssClassName("output-window")}>
                                        <div className="row">
                                            <div className="pv-3">
                                                <Tabs
                                                    type="card"
                                                    activeKey={tabActiveKey}
                                                    onChange={handleTabChange}
                                                    items={tabs.map((_, i) => {
                                                        const id = tabs[i];
                                                        return {
                                                            label: id,
                                                            key: id,
                                                            children: (
                                                                <>
                                                                    {id === 'Console' &&
                                                                        <div className="row">
                                                                            {showOutputWindow && !uploadInProgress && (
                                                                                <div
                                                                                    ref={outputRef}
                                                                                    className="col border rounded p-3 text-bg-dark"
                                                                                    style={{
                                                                                        height: 605,
                                                                                        overflowY: "auto",
                                                                                        overflowX: "auto",
                                                                                    }}
                                                                                >
                                                                                    <pre style={{ overflow: null, color: '#00ff00' }}>
                                                                                        {uploadOutput}
                                                                                    </pre>
                                                                                </div>
                                                                            )}

                                                                            {uploadInProgress && (
                                                                                <div
                                                                                    className="col border rounded p-1 bg-light-subtle"
                                                                                    style={{ minHeight: 500 }}
                                                                                >
                                                                                    <div className="col mt-4 h5 text-center">
                                                                                        Uploading to board{" "}
                                                                                        <Spin
                                                                                            indicator={
                                                                                                <LoadingOutlined
                                                                                                    style={{ fontSize: 24 }}
                                                                                                    spin
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col text-center fw-light small mt-2">
                                                                                        Compiling and uploading your sketch to the Arduino board.
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    }
                                                                    {id === 'Serial Monitor' &&
                                                                        <SerialMonitor
                                                                            isUploadInProgress={uploadInProgress || boardUploadInProgress}
                                                                        />
                                                                    }
                                                                    {id === 'Ai Chatbot' && (global_var.role_id !== global_var.STUDENT_ROLE_ID || enabledAIfeatures.includes('Ai chatbot')) &&
                                                                        <AiChatBot
                                                                            fileMetaList={fileMetaList}
                                                                            ideProjectId={ideProjectId}
                                                                            selectedFileId={selectedFile ? selectedFile : false}
                                                                            selectedTheme={selectedTheme}
                                                                            handleChatBotInsert={handleChatBotInsert}
                                                                        />
                                                                    }
                                                                </>
                                                            ),
                                                        };
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            false
                        )}
                    </div>
                </div>

                <Modal
                    open={mobileFileManagerModal}
                    onCancel={closeMobileFileManagerModal}
                    footer={null}
                    width={'60%'}
                    bodyStyle={{ height: '90vh', overflow: 'auto', padding: '10px' }}
                    style={{ marginLeft: '0px', marginTop: '-90px' }}
                >
                    {ideProjectDetails && (
                        <FileManager
                            fileStructure={fileMetaList}
                            defaultSelectedKey={ideProjectDetails.lastAccessedFileId}
                            ideProjectId={ideProjectId}
                            ideProjectDetails={ideProjectDetails}
                            nodeSelected={nodeSelected}
                            handleNewFile={handleNewFile}
                            handleFileDeleted={handleFileDeleted}
                            handleFileRenamed={handleFileRenamed}
                            readOnlyMode={readOnlyMode}
                            projectStack="arduino"
                        />
                    )}
                </Modal>

                {newProjectModal && <CreateIDEProjectModal projectStack="arduino" />}

                {simulatorHex && (
                    <ArduinoSimulator
                        hexBase64={simulatorHex}
                        onClose={() => setSimulatorHex(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default ArduinoIDE;